import { BN } from 'fbonds-core'
import { calcOptimisticBasedOnBulkSimulation } from 'fbonds-core/lib/fbond-protocol/functions/perpetual'
import { BanxStakeState } from 'fbonds-core/lib/fbond-protocol/types'
import { chain, groupBy, keyBy } from 'lodash'

import { staking } from '@banx/api/common'
import { StakingSimulatedAccountsResult } from '@banx/transactions/staking'

export const convertStakingSimulatedAccountsToMergeData = (
  simulatedResults: StakingSimulatedAccountsResult[],
): {
  banxStakingSettings: staking.BanxStakingSettings[]
  banxTokenStakes: staking.BanxTokenStake[]
  banxAdventures: staking.BanxAdventure[]
  banxAdventureSubscriptions: staking.BanxAdventureSubscription[]
  banxStakes: staking.BanxStake[]
  banxSettings: staking.BanxStakingSettings[]
  banxAdventureSnapshot: staking.BanxAdventureSnapshot[]
} => {
  const transformer = <T>(
    simulations: StakingSimulatedAccountsResult[],
    field: keyof StakingSimulatedAccountsResult,
  ): T[] =>
    chain(simulations)
      .map((simulation) => simulation?.[field])
      .flatten()
      .compact()
      .value() as T[]

  return {
    banxStakingSettings: transformer(simulatedResults, 'banxStakingSettings'),
    banxTokenStakes: transformer(simulatedResults, 'banxTokenStake'),
    banxAdventures: transformer(simulatedResults, 'banxAdventures'),
    banxAdventureSubscriptions: transformer(simulatedResults, 'banxAdventureSubscriptions'),
    banxStakes: transformer(simulatedResults, 'banxStake'),
    banxSettings: transformer(simulatedResults, 'banxStakingSettings'),
    banxAdventureSnapshot: transformer(simulatedResults, 'banxAdventureSnapshot'),
  }
}

export const mergeWithBanxStakingInfo = (
  banxStakingInfo: staking.BanxStakingInfo,
  dataToMerge: Partial<{
    banxTokenStakes: staking.BanxTokenStake[]
    banxAdventures: staking.BanxAdventure[]
    banxAdventureSubscriptions: staking.BanxAdventureSubscription[]
    banxStakes: staking.BanxStake[]
    banxSettings: staking.BanxStakingSettings[]
    banxAdventureSnapshot: staking.BanxAdventureSnapshot[]
  }>,
): staking.BanxStakingInfo => {
  const {
    banxTokenStakes = [],
    banxAdventures = [],
    banxAdventureSubscriptions = [],
    banxStakes = [],
    banxSettings = [],
    banxAdventureSnapshot = [],
  } = dataToMerge

  const mergedAdventures = mergeAdventures(banxStakingInfo.adventures, banxAdventures)

  const mergedTokenStake = mergeTokenStakes(banxStakingInfo.tokenStake, banxTokenStakes)
  const mergedNftStakes = mergeBanxNftStakes(banxStakingInfo.nftStakes, banxStakes)
  const mergedSettings = mergeBanxSettings(banxStakingInfo.settings, banxSettings)

  const mergedAdventureSnapshots = mergeAdventuresSnapshots(
    banxStakingInfo.subscriptionSnapshots,
    banxAdventureSnapshot,
  )

  const mergedAdventureSubscription = mergeBanxAdventureSubscriptions(
    banxStakingInfo?.subscription,
    banxAdventureSubscriptions,
  )

  return {
    ...banxStakingInfo,
    adventures: mergedAdventures,
    tokenStake: mergedTokenStake,
    nftStakes: mergedNftStakes,
    settings: mergedSettings,
    subscription: mergedAdventureSubscription,
    subscriptionSnapshots: mergedAdventureSnapshots,
  }
}

const mergeAdventures = (
  base: staking.BanxAdventure[],
  updates: staking.BanxAdventure[],
): staking.BanxAdventure[] => {
  if (!updates?.length) return base

  const updatesByPk = groupBy(updates, ({ publicKey }) => publicKey.toBase58())

  return chain(base)
    .map((adventure) => {
      const relevantUpdates = updatesByPk[adventure.publicKey.toBase58()] ?? []
      if (!relevantUpdates.length) return adventure

      //? Remove non-BN fields to prevent errors during diff calculation
      const cleanedBase = pickBNFields(adventure) as staking.BanxAdventure
      const cleanedUpdates = relevantUpdates.map(pickBNFields) as staking.BanxAdventure[]

      const merged = calcOptimisticBasedOnBulkSimulation<staking.BanxAdventure>(
        cleanedBase,
        cleanedUpdates,
        ['periodEndingAt', 'periodStartedAt'],
      )

      return { ...adventure, ...merged }
    })
    .unionWith(updates, (a, b) => a.publicKey.toBase58() === b.publicKey.toBase58())
    .value()
}

const mergeAdventuresSnapshots = (
  base: staking.BanxAdventureSnapshot[],
  updates: staking.BanxAdventureSnapshot[],
): staking.BanxAdventureSnapshot[] => {
  if (!updates?.length) return base

  const updatesByPk = groupBy(updates, ({ publicKey }) => publicKey.toBase58())

  return chain(base)
    .map((adventure) => {
      const relevantUpdates = updatesByPk[adventure.publicKey.toBase58()] ?? []
      if (!relevantUpdates.length) return adventure

      //? Remove non-BN fields to prevent errors during diff calculation
      const cleanedBase = pickBNFields(adventure) as staking.BanxAdventureSnapshot
      const cleanedUpdates = relevantUpdates.map(pickBNFields) as staking.BanxAdventureSnapshot[]

      const merged = calcOptimisticBasedOnBulkSimulation<staking.BanxAdventureSnapshot>(
        cleanedBase,
        cleanedUpdates,
        ['lastTransactedAt'],
      )

      return { ...adventure, ...merged }
    })
    .unionWith(updates, (a, b) => a.publicKey.toBase58() === b.publicKey.toBase58())
    .value()
}

const mergeBanxNftStakes = (
  base: staking.BanxStake[],
  updates: staking.BanxStake[],
): staking.BanxStake[] => {
  if (!updates?.length) return base

  const updatesByMint = keyBy(updates, ({ nftMint }) => nftMint)

  return chain(base)
    .map((oldStake) => {
      const fresh = updatesByMint[oldStake.nftMint]
      if (!fresh) return oldStake
      if (fresh.banxStakeState !== BanxStakeState.Staked) return undefined
      return { ...oldStake, ...fresh }
    })
    .compact()
    .unionWith(
      updates.filter((st) => st.banxStakeState === BanxStakeState.Staked),
      (a, b) => a.nftMint === b.nftMint,
    )
    .value()
}

const mergeTokenStakes = (
  base: staking.BanxTokenStake | null | undefined,
  updates: staking.BanxTokenStake[],
): staking.BanxTokenStake | null => {
  if (!updates.length) {
    return base ?? null
  }

  //? Remove non-BN fields to prevent errors during diff calculation
  const cleanedBase = base ? pickBNFields(base) : null
  const cleanedUpdates = updates.map((u) => pickBNFields(u)) as staking.BanxTokenStake[]

  const ignoreFields = ['stakedAt', 'unstakedAt', 'nftsStakedAt', 'nftsUnstakedAt']

  //? If there is no base, merge all updates starting from the first one
  if (!cleanedBase) {
    if (cleanedUpdates.length === 1) {
      return cleanedUpdates[0] ?? null
    }

    const [first, ...rest] = cleanedUpdates
    return calcOptimisticBasedOnBulkSimulation<staking.BanxTokenStake>(first, rest, ignoreFields)
  }

  return calcOptimisticBasedOnBulkSimulation<staking.BanxTokenStake>(
    cleanedBase as staking.BanxTokenStake,
    cleanedUpdates,
    ignoreFields,
  )
}

const mergeBanxAdventureSubscriptions = (
  base: staking.BanxAdventureSubscription | null | undefined,
  updates: staking.BanxAdventureSubscription[],
): staking.BanxAdventureSubscription | null => {
  if (!updates.length) {
    return base ?? null
  }

  //? Remove non-BN fields to prevent errors during diff calculation
  const cleanedBase = base ? pickBNFields(base) : null
  const cleanedUpdates = updates.map((u) => pickBNFields(u)) as staking.BanxAdventureSubscription[]

  const ignoreFields = ['subscribedAt', 'unsubscribedAt', 'harvestedAt']

  //? If there is no base, merge all updates starting from the first one
  if (!cleanedBase) {
    if (cleanedUpdates.length === 1) {
      return cleanedUpdates[0] ?? null
    }

    const [first, ...rest] = cleanedUpdates
    return calcOptimisticBasedOnBulkSimulation<staking.BanxAdventureSubscription>(
      first,
      rest,
      ignoreFields,
    )
  }

  return calcOptimisticBasedOnBulkSimulation(
    cleanedBase as staking.BanxAdventureSubscription,
    cleanedUpdates,
    ignoreFields,
  )
}

export const mergeBanxSettings = (
  base: staking.BanxStakingSettings,
  updates: staking.BanxStakingSettings[],
): staking.BanxStakingSettings => {
  if (!updates.length) {
    return base
  }

  //? Remove non-BN fields to prevent errors during diff calculation
  const cleanedBase = pickBNFields(base)
  const cleanedUpdates = updates.map((u) => pickBNFields({ ...base, ...u }))

  return calcOptimisticBasedOnBulkSimulation<staking.BanxStakingSettings>(
    cleanedBase as staking.BanxStakingSettings,
    cleanedUpdates as staking.BanxStakingSettings[],
    ['maxTokenStakeAmount', 'tokensPerPartnerPoints', 'tokensPerWeek', 'hadesPerWeek'],
  )
}

const pickBNFields = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([, val]) => BN.isBN(val))) as Partial<T>
}
